import { adlandApiUrl } from "@adland/data";
import type { AdAuth, AdConsent, AdEvent, AdEventType } from "../types";

interface TrackingPayload {
  slot: string;
  chainId: number;
  auth?: AdAuth;
  placement?: string;
  cid?: string | null;
  empty?: boolean;
  /** API base URL override; falls back to the built-in default. */
  endpoint?: string;
  /** Consent gate for the network beacon. Default: "granted". */
  consent?: AdConsent;
  /** Local callback fired for every event regardless of consent. */
  onEvent?: (event: AdEvent) => void;
  /**
   * Resolves an auth token to attach to the beacon (e.g. a Farcaster Quick Auth
   * JWT). Injected by the caller so this module stays free of any auth SDK —
   * a web-only build that never provides one ships none of that code.
   */
  getAuthToken?: () => Promise<string | null>;
}

/** Track which slot+url combos have already been counted this session */
const tracked = new Set<string>();

/** How long a creative must stay ≥50% visible before a verified view fires. */
const VIEWABILITY_DWELL_MS = 1000;

/** Map the precise event type onto the wire type the API accepts today. */
function wireType(eventType: AdEventType): "view" | "click" {
  return eventType === "view" ? "view" : "click";
}

/** The API base URL for a payload. */
function baseUrl(data: TrackingPayload): string {
  return data.endpoint ?? adlandApiUrl;
}

/**
 * Fire the publisher's local `onEvent`. Always runs, independent of consent or
 * network — it is the publisher's own data path.
 */
function mirror(
  eventType: AdEventType,
  data: TrackingPayload,
  verified?: boolean,
): void {
  data.onEvent?.({
    type: eventType,
    slot: data.slot,
    chainId: data.chainId,
    cid: data.cid ?? null,
    placement: data.placement,
    empty: data.empty ?? false,
    auth: data.auth ?? "none",
    ...(verified === undefined ? {} : { verified }),
  });
}

/** POST an unverified event to the legacy /track endpoint. */
async function legacyBeacon(
  eventType: AdEventType,
  data: TrackingPayload,
): Promise<void> {
  const auth: AdAuth = data.auth ?? "none";
  let token: string | null = null;
  if (auth === "farcaster" && data.getAuthToken) {
    token = await data.getAuthToken();
  }

  try {
    fetch(`${baseUrl(data)}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: wireType(eventType),
        slot: data.slot,
        cid: data.cid ?? null,
        domain: window.location.hostname,
        auth,
        // Both of these were being collected by the caller, declared on
        // `TrackingPayload`, read by the `/track` route — and never actually
        // put on the wire. Every legacy event therefore landed with a null
        // chain and a null placement, which is why no unverified event could
        // be attributed to where it happened. The verified `/v1/event` path
        // has always sent them; this one just never did.
        ...(typeof data.chainId === "number" ? { chainId: data.chainId } : {}),
        ...(data.placement ? { placement: data.placement } : {}),
        ...(token ? { token } : {}),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

/**
 * Notify the publisher's local `onEvent` and, when consent allows, POST the
 * event to the legacy /track endpoint. The beacon is gated on
 * `consent === "granted"`; `onEvent` always fires.
 */
async function sendEvent(
  eventType: AdEventType,
  data: TrackingPayload,
): Promise<void> {
  if (typeof window === "undefined") return;
  mirror(eventType, data);
  if ((data.consent ?? "granted") !== "granted") return;
  await legacyBeacon(eventType, data);
}

/**
 * POST a verified view to /v1/event. Resolves true only on a 2xx response.
 *
 * Carries the auth token as well as the nonce, because the two prove different
 * things and the impression is worth more for having both: the nonce says the
 * view came from the origin it was issued to, the token says a real Farcaster
 * account was looking. Sending only the nonce — which this did — meant every
 * verified impression was recorded as anonymous, so switching the verified path
 * on would have zeroed the authed figure rather than enriching it.
 */
async function verifiedBeacon(
  data: TrackingPayload,
  nonce: string,
  viewableMs: number,
): Promise<string | null> {
  const auth: AdAuth = data.auth ?? "none";
  let token: string | null = null;
  if (auth === "farcaster" && data.getAuthToken) {
    token = await data.getAuthToken();
  }

  try {
    const res = await fetch(`${baseUrl(data)}/v1/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nonce,
        type: "view",
        slot: data.slot,
        cid: data.cid ?? null,
        placement: data.placement,
        viewable_ms: viewableMs,
        chainId: data.chainId,
        auth,
        // The JWT is bound to the mini app's own domain, so the API needs to be
        // told which domain to verify it against — the same contract as the
        // legacy path.
        ...(token ? { token, domain: window.location.hostname } : {}),
      }),
      keepalive: true,
    });
    if (!res.ok) return null;

    /*
     * The credential a click will have to present, or "" for a verified view
     * that produced none.
     *
     * The empty string matters: it is how "the view was accepted" stays
     * distinct from "the view failed" (null) when the response carries no
     * token — an older API that does not mint them, or a 204. Collapsing the
     * two would make a successful view report itself unverified.
     *
     * `res.json` is checked before it is called rather than only wrapped,
     * because a body-less response and a hand-rolled test double both reach
     * here without one, and calling `undefined` throws out of the whole
     * function instead of degrading.
     */
    const json =
      typeof res.json === "function"
        ? ((await res.json().catch(() => null)) as {
            clickToken?: unknown;
          } | null)
        : null;
    return typeof json?.clickToken === "string" ? json.clickToken : "";
  } catch {
    return null;
  }
}

/**
 * Click credentials, by slot.
 *
 * Held in the module rather than passed around because the view and the click
 * are separate events with no call path between them: an impression fires from
 * an IntersectionObserver and a click from a listener, and the only thing they
 * share is the slot.
 *
 * Not persisted. A credential is worth 30 minutes and three clicks, so carrying
 * it across page loads would buy a little verified traffic and leave a token in
 * storage on a publisher's domain — a bad trade for something the next
 * impression re-mints anyway.
 */
const clickCredentials = new Map<string, string>();

/**
 * Ask for a nonce bound to this slot and the request's origin.
 *
 * Extracted from the impression path, which had it inline, once the click path
 * needed the same request. Two spellings of "get me a nonce" would have been
 * two places to keep the body shape and the failure handling in step — and the
 * failure handling is the load-bearing part: a null here has to degrade to the
 * legacy beacon rather than lose the event.
 */
function requestNonce(data: TrackingPayload): Promise<string | null> {
  return fetch(`${baseUrl(data)}/v1/serve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slot: data.slot }),
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j: { nonce?: string } | null) => j?.nonce ?? null)
    .catch(() => null);
}

/**
 * Deliver a view: try the verified /v1 beacon, fall back to legacy /track so an
 * impression is never lost. Mirrors `onEvent` once, with the true `verified`
 * outcome. Exported for testing. Consent gates the network, not the mirror.
 */
export async function sendVerifiedView(
  data: TrackingPayload,
  nonce: string | null,
  viewableMs: number,
): Promise<boolean> {
  let verified = false;
  if ((data.consent ?? "granted") === "granted") {
    const credential = nonce
      ? await verifiedBeacon(data, nonce, viewableMs)
      : null;
    verified = credential !== null;
    if (credential) clickCredentials.set(data.slot, credential);
    if (!verified) await legacyBeacon("view", data);
  }
  mirror("view", data, verified);
  return verified;
}

/**
 * Track a unique impression (once per slot per page load).
 */
export function trackImpression(
  element: HTMLElement | null,
  data: TrackingPayload,
): (() => void) | undefined {
  if (!element || typeof window === "undefined") return;

  const key = `impression:${data.slot}:${window.location.href}`;
  if (tracked.has(key)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !tracked.has(key)) {
          tracked.add(key);
          sendEvent("view", data);
          observer.disconnect();
        }
      }
    },
    { threshold: 0.5 },
  );

  observer.observe(element);

  return () => observer.disconnect();
}

/**
 * Track a verified impression: request a nonce as the creative mounts, then —
 * after the element stays ≥50% visible for the viewability dwell — POST a
 * verified /v1 event (falling back to legacy /track). Shares the dedup key with
 * `trackImpression`, so a slot is never counted twice.
 */
export function trackVerifiedImpression(
  element: HTMLElement | null,
  data: TrackingPayload,
): (() => void) | undefined {
  if (!element || typeof window === "undefined") return;

  const key = `impression:${data.slot}:${window.location.href}`;
  if (tracked.has(key)) return;

  // Request the nonce immediately — one round-trip, resolved by the time the
  // dwell elapses.
  const noncePromise = requestNonce(data);

  let dwellTimer: ReturnType<typeof setTimeout> | undefined;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (tracked.has(key) || dwellTimer) continue;
          dwellTimer = setTimeout(async () => {
            if (tracked.has(key)) return;
            tracked.add(key);
            observer.disconnect();
            const nonce = await noncePromise;
            await sendVerifiedView(data, nonce, VIEWABILITY_DWELL_MS);
          }, VIEWABILITY_DWELL_MS);
        } else if (dwellTimer) {
          // Left the viewport before the dwell elapsed — reset.
          clearTimeout(dwellTimer);
          dwellTimer = undefined;
        }
      }
    },
    { threshold: 0.5 },
  );

  observer.observe(element);

  return () => {
    observer.disconnect();
    if (dwellTimer) clearTimeout(dwellTimer);
  };
}

/**
 * Track a click event. `eventType` distinguishes a click on a loaded ad
 * ("click") from a click on an empty slot ("click-empty").
 */
/**
 * Deliver a click, verified where the view before it left a credential.
 *
 * ── Why this stopped being one line ──────────────────────────────────────────
 *
 * It was `sendEvent(eventType, data)`, which meant the legacy `/track` beacon
 * and nothing else — so every click on the platform was recorded unverified.
 * That is how a domain came to report 106 clicks against 61 views: the endpoint
 * takes a POST and writes a row, and there was no stronger path for an honest
 * click to take, so there was nothing to compare a dishonest one against.
 *
 * A click now presents the credential its own view was given. The server mints
 * those only when it accepts a view, so a click that never followed one has
 * nothing to send and falls back to the legacy path — where it still counts,
 * but as `verified: false`, which is a number a publisher's payout can discount.
 *
 * `click-empty` deliberately keeps the old path. It is a click on the "Your ad
 * here" state, which is a lead rather than a paid event, and nobody is inflating
 * a metric that pays nothing.
 */
export async function trackClick(
  eventType: "click" | "click-empty",
  data: TrackingPayload,
): Promise<void> {
  if (typeof window === "undefined") return;
  mirror(eventType, data);
  if ((data.consent ?? "granted") !== "granted") return;

  const credential =
    eventType === "click" ? clickCredentials.get(data.slot) : undefined;
  if (credential) {
    const nonce = await requestNonce(data);
    if (nonce && (await verifiedClick(data, nonce, credential))) return;
  }

  await legacyBeacon(eventType, data);
}

/**
 * POST a click to /v1/event with both proofs.
 *
 * The nonce says this request came from the origin it was issued to; the
 * credential says a view happened first. Neither is sufficient alone — anyone
 * can ask `/v1/serve` for a nonce without rendering anything, and a credential
 * without a fresh nonce would be replayable.
 */
async function verifiedClick(
  data: TrackingPayload,
  nonce: string,
  clickToken: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl(data)}/v1/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nonce,
        clickToken,
        type: "click",
        slot: data.slot,
        cid: data.cid ?? null,
        placement: data.placement,
        chainId: data.chainId,
        auth: "none",
      }),
      keepalive: true,
    });
    // A spent credential comes back 403, and that is the system working: the
    // click still lands below, on the legacy path, marked unverified.
    return res.ok;
  } catch {
    return false;
  }
}
